import "./style.scss";

import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from "@mui/material";
import { formatDatesChosen } from "components/Payment";
import Pagination from "components/shared/Pagination";
import SmallSearchBar from "components/shared/SmallSearchBar";
import StatusSelect from "components/shared/StatusSelect";
import { paymentColorRules, paymentOptions } from "components/shared/StatusSelect/utils";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { H3 } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import usePagination from "hooks/usePagination";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IArticle } from "store/articleStore";
import { getOldestArticlePaid, putArticleDate, putArticlePaidBeneficiary } from "store/articleStore";
import { fetchPaymentsBetweenDates } from "store/paymentSlice";
import { useAppDispatch } from "store/store";
import type { IDatesRange } from "types";

import variables from "../../../../assets/styles/_variables.module.scss";

const tHeadStyles = {
    maxWidth: 200,
    fontSize: "12px",
    color: variables["foreground"],
    width: "min-content",
    padding: "14px 20px",
    backgroundColor: "#F8F8F8",
    fontFamily: "'Poppins', sans-serif",
    border: "none",
};

interface IPressPaymentDetails {
    datesRange: IDatesRange | undefined;
    paymentData: IArticle[];
}

const PressPaymentDetails = ({ datesRange, paymentData }: IPressPaymentDetails) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [filter, setFilter] = useState("");

    const filteredData = useMemo(() => {
        return paymentData.filter((item) => item.name.toLowerCase().includes(filter.toLowerCase()));
    }, [paymentData]);

    const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(event.target.value);
    };

    const [dividedByPage, handleChangePage, handleChangeRowsPerPage, page, rowsPerPage] = usePagination(filteredData);

    const handleChangePaymentStatus = (value: 0 | 1 | 2, item: IArticle) => {
        const paymentData = {
            paidStatus: value,
            paymentDate: value == 0 ? null : new Date(),
        };

        dispatch(
            putArticlePaidBeneficiary({
                beneficiarId: item.beneficiaryId,
                articleId: item.id,
                paymentData,
            }),
        ).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled" && datesRange) {
                dispatch(
                    fetchPaymentsBetweenDates({
                        startDate: datesRange.startDate.toLocaleDateString("en-CA"),
                        endDate: datesRange.endDate.toLocaleDateString("en-CA"),
                    }),
                );
            }
        });
    };

    const handleChangePaymentDate = async (value?: Date | null, titleId?: number, articleId?: number) => {
        if (titleId == undefined || articleId == undefined || value == null || value == undefined) return;
        const firstResponse = await dispatch(putArticleDate({ titleId, articleId, paymentDate: value }));
        if (firstResponse.meta.requestStatus === "fulfilled") {
            await dispatch(getOldestArticlePaid());
        }
    };

    return (
        <Fragment>
            <H3>
                {t("Payment.Payments")} {formatDatesChosen(datesRange)} :
            </H3>
            <SmallSearchBar
                filter={filter}
                onSearch={handleChangeFilter}
                placeholder={t("Payment.DetailsPlaceholder")}
            />
            <Paper
                sx={{
                    padding: 0,
                    width: "100%",
                    overflow: "hidden",
                    marginBottom: "20px",
                    border: "none",
                    borderRadius: variables["border-raduis"],
                    boxShadow: "none",
                }}
            >
                <TableContainer className="press-payment-details">
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderRadius: variables["border-radius"],
                                }}
                            >
                                <TableCell
                                    sx={{
                                        ...tHeadStyles,
                                        borderBottomLeftRadius: variables["border-radius"],
                                    }}
                                >
                                    {t("Payment.Article")}
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="center">
                                    {t("Payment.Status")}
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="center">
                                    {t("Payment.Date")}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...tHeadStyles,
                                        borderBottomRightRadius: variables["border-radius"],
                                    }}
                                    align="right"
                                >
                                    {t("Payment.Amount")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dividedByPage.map((item, index) => {
                                return (
                                    <TableRow tabIndex={-1} key={index} className="benef-table-row">
                                        <TableCell sx={{ padding: "8px 0 8px 8px" }} className="payment-article-name">
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "8px",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {/* <Avatar>{item.name[0]}</Avatar> */}
                                                {item.name}
                                            </div>
                                        </TableCell>
                                        <TableCell sx={{ padding: "8px 0" }} align="center" className="payment-status">
                                            {item.paidStatus != undefined && (
                                                <StatusSelect
                                                    value={item.paidStatus}
                                                    options={paymentOptions()}
                                                    onChange={(newPaymentValue: 0 | 1 | 2) =>
                                                        handleChangePaymentStatus(newPaymentValue, item)
                                                    }
                                                    colorRules={paymentColorRules}
                                                    disabled
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell
                                            sx={{ padding: "8px 0", maxWidth: "125px" }}
                                            align="center"
                                            className="payment-date-picker"
                                        >
                                            <StyledDatePicker
                                                value={item.paymentDate}
                                                onChange={(value) =>
                                                    handleChangePaymentDate(value, item.titleId, item.id)
                                                }
                                                inputFormat="dd/MM/yyyy"
                                                renderInput={({ inputProps, ...props }) => (
                                                    <StyledTextField
                                                        {...props}
                                                        inputProps={{
                                                            ...inputProps,
                                                            readOnly: true,
                                                        }}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{ padding: "8px 0px" }}
                                            align="right"
                                            className="payment-net-value"
                                        >
                                            <span>
                                                {formatNumber((item.netToPay || 0) / 100, {
                                                    isCurrency: true,
                                                    always2Digit: true,
                                                })}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component={() => (
                        <Pagination
                            rowsPerPageOptions={[10, 25, 100]}
                            rowsPerPage={rowsPerPage}
                            currentPageSize={filteredData.length}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            type="paymentInfo"
                            count={filteredData.length}
                        />
                    )}
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                />
            </Paper>
        </Fragment>
    );
};

export default PressPaymentDetails;
